import { StartupScreen } from "@cliqsuite/ui";
import { useEffect, useState, type ReactNode } from "react";
import { PowerSyncContext, useStatus } from "@powersync/react";
import { getAuthToken, reconnectWhenModulesChange, syncConnector } from "@cliqsuite/db/powersync";
import { db } from "../lib/db";
import { flushOutbox, importLegacyPendingSales } from "../lib/outbox";

// Retry the offline-sale outbox on a timer too, on top of the `online` event and the
// post-queue flush: catches connectivity that "looks" online while fetches keep failing.
const OUTBOX_FLUSH_INTERVAL_MS = 25_000;

let connected = false;
/** Start syncing once a device-scope token exists: on boot for a paired till, or right after pairing. */
export function ensurePosSyncStarted(): void {
  if (connected || !getAuthToken()) return;
  connected = true;
  void db.connect(syncConnector());
  if (!watchingModules) reconnectWhenModulesChange(db, syncConnector);
  watchingModules = true;
}
let watchingModules = false;

/** Stop syncing and forget synced data, keeping queued offline sales (a local-only table). */
export async function stopPosSync(): Promise<void> {
  connected = false;
  await db.disconnectAndClear({ clearLocal: false });
}

/**
 * True once this till has completed a full sync — ever, not just this page load. PowerSync
 * remembers it in the local database, so a returning till shows its data at once, offline too.
 */
export function usePosSyncReady(): boolean {
  return useStatus().hasSynced ?? false;
}
// Bucket priorities in powersync/sync-rules.yaml: these land before the rest of the store.
const PRIORITY: Record<string, number> = { staff: 1, products: 2 };

/** True once `table` has landed. Staff and the menu don't wait for order history. */
export function usePosShapeSyncReady(table: string): boolean {
  const status = useStatus();
  const priority = PRIORITY[table];
  return !!status.hasSynced || (priority !== undefined && !!status.statusForPriority(priority)?.hasSynced);
}
export const readSyncedBefore = (): boolean => !!db.currentStatus.hasSynced;

export function PosDbProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Sales the PGlite version still had queued move over before anything else runs.
    void importLegacyPendingSales()
      .catch((err) => console.error("Couldn't import queued offline sales from the old database:", err))
      .finally(() => {
        if (cancelled) return;
        setReady(true);
        ensurePosSyncStarted();
        void flushOutbox();
      });
    const onOnline = () => void flushOutbox();
    window.addEventListener("online", onOnline);
    // Per-device jitter so tills that lost connectivity together don't retry in lockstep.
    const interval = setInterval(() => void flushOutbox(), OUTBOX_FLUSH_INTERVAL_MS + Math.random() * 10_000);
    return () => {
      cancelled = true;
      window.removeEventListener("online", onOnline);
      clearInterval(interval);
    };
  }, []);

  if (!ready) return <StartupScreen app="POS" label="Getting the register ready" />;
  return <PowerSyncContext.Provider value={db}>{children}</PowerSyncContext.Provider>;
}
